import api from '../../../services/api';
import type { 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse, 
  User, 
  Team 
} from '../../../types';

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    // 转换为后端期望的格式
    const loginData = {
      name: credentials.username,
      password: credentials.password
    };
    
    const response = await api.post('/auth/login', loginData);

    // 转换后端响应格式为前端期望的格式
    const backendData = response.data;
    return {
      access_token: backendData.token,
      user: {
        id: backendData.user.id,
        name: backendData.user.name,
        nickname: backendData.user.nickname,
        username: backendData.user.name, // 保持向后兼容
        email: backendData.user.email,
        isAdmin: backendData.user.isAdmin,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        teamMemberships: backendData.user.teamMemberships || []
      },
      team: backendData.user.currentTeam ? {
        id: backendData.user.currentTeam.id,
        name: backendData.user.currentTeam.name,
        description: backendData.user.currentTeam.description || '',
        inviteCode: backendData.user.currentTeam.inviteCode || '',
        createdAt: backendData.user.currentTeam.createdAt || new Date().toISOString(),
        updatedAt: backendData.user.currentTeam.updatedAt || new Date().toISOString()
      } : null
    };
  },

  async sendEmailCode(email: string): Promise<void> {
    await api.post('/auth/email-code', { email });
  },

  async register(data: RegisterRequest): Promise<AuthResponse> {
    // 转换为后端期望的格式
    const registerData = {
      name: data.username || undefined,
      password: data.password,
      email: data.email,
      code: data.code,
      teamName: data.teamName,
      inviteCode: data.inviteCode
    };
    
    const response = await api.post('/auth/register', registerData);

    // 转换后端响应格式为前端期望的格式
    const backendData = response.data;
    return {
      access_token: backendData.token,
      user: {
        id: backendData.user.id,
        name: backendData.user.name,
        nickname: backendData.user.nickname,
        username: backendData.user.name, // 保持向后兼容
        email: backendData.user.email,
        isAdmin: backendData.user.isAdmin,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        teamMemberships: backendData.user.teamMemberships || []
      },
      team: backendData.user.currentTeam ? {
        id: backendData.user.currentTeam.id,
        name: backendData.user.currentTeam.name,
        description: backendData.user.currentTeam.description || '',
        inviteCode: backendData.user.currentTeam.inviteCode || '',
        createdAt: backendData.user.currentTeam.createdAt || new Date().toISOString(),
        updatedAt: backendData.user.currentTeam.updatedAt || new Date().toISOString()
      } : null
    };
  },

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('team');
  },

  getStoredUser(): User | null {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  },

  getStoredTeam(): Team | null {
    try {
      const team = localStorage.getItem('team');
      return team ? JSON.parse(team) : null;
    } catch {
      return null;
    }
  },

  getStoredToken(): string | null {
    return localStorage.getItem('token');
  },

  storeAuthData(data: AuthResponse): void {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('team', JSON.stringify(data.team));
  },

  updateStoredUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me');
    const backendData = response.data;

    // 转换后端响应格式为前端期望的格式
    return {
      id: backendData.id,
      name: backendData.name,
      nickname: backendData.nickname,
      username: backendData.name, // 保持向后兼容
      email: backendData.email,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      teamMemberships: backendData.teamMemberships || []
    };
  },

  isAuthenticated(): boolean {
    return !!this.getStoredToken() && !!this.getStoredUser();
  }
};